import type { ReactNode } from 'react';
import { MerchantScreenFrame as ScreenFrame } from './MerchantScreenFrame.js';
import { DetailPageShell } from './DetailSection.js';
import { DetailFooterNav } from './DetailUi.js';

export function MissingContextFrame({
  screenId,
  title,
  message,
  hint,
  backTo,
  backLabel,
}: {
  screenId: string;
  title: string;
  message: string;
  hint?: string;
  backTo: string;
  backLabel: string;
}) {
  return (
    <ScreenFrame screenId={screenId} title={title} state="empty" emptyMessage={message}>
      <DetailPageShell>
        {hint ? <p className="m-0 text-body-sm text-muted">{hint}</p> : null}
        <DetailFooterNav to={backTo} label={backLabel} />
      </DetailPageShell>
    </ScreenFrame>
  );
}

export function PermissionDeniedFrame({
  screenId,
  title,
  message = 'You do not have permission to view this page.',
  backTo,
  backLabel,
  children,
}: {
  screenId: string;
  title: string;
  message?: string;
  backTo: string;
  backLabel: string;
  children?: ReactNode;
}) {
  return (
    <ScreenFrame screenId={screenId} title={title} state="idle" showTitle>
      <DetailPageShell>
        <div
          className="mer-permission-denied rounded-control border border-line bg-raised p-field"
          data-testid="permission-gate-denied"
          role="status"
        >
          <p className="m-0 text-body">{message}</p>
          {children}
        </div>
        <DetailFooterNav to={backTo} label={backLabel} />
      </DetailPageShell>
    </ScreenFrame>
  );
}

export function MissingProductContextFrame({
  screenId,
  title,
  backTo,
  backLabel,
}: {
  screenId: string;
  title: string;
  backTo: string;
  backLabel: string;
}) {
  return (
    <MissingContextFrame
      screenId={screenId}
      title={title}
      message="Select a product to continue."
      hint="Open this screen from the product list or product editor."
      backTo={backTo}
      backLabel={backLabel}
    />
  );
}

export function MissingStatementContextFrame({
  screenId,
  title,
  backTo,
  backLabel,
}: {
  screenId: string;
  title: string;
  backTo: string;
  backLabel: string;
}) {
  return (
    <MissingContextFrame
      screenId={screenId}
      title={title}
      message="Select a statement to view its detail."
      hint="Open a statement from the statements list."
      backTo={backTo}
      backLabel={backLabel}
    />
  );
}

export function MissingStaffContextFrame({
  screenId,
  title,
  backTo,
  backLabel,
}: {
  screenId: string;
  title: string;
  backTo: string;
  backLabel: string;
}) {
  return (
    <MissingContextFrame
      screenId={screenId}
      title={title}
      message="Select a staff member to view their profile."
      hint="Open a team member from the staff list."
      backTo={backTo}
      backLabel={backLabel}
    />
  );
}

export function MissingPickItemContextFrame({
  screenId,
  title,
  orderId,
  backTo,
  backLabel,
}: {
  screenId: string;
  title: string;
  orderId: string;
  backTo: string;
  backLabel: string;
}) {
  return (
    <MissingContextFrame
      screenId={screenId}
      title={title}
      message="Select a pick line to continue."
      hint={
        orderId
          ? 'Choose an item from the pick list for this order.'
          : 'Choose an order and pick line from the pick list.'
      }
      backTo={backTo}
      backLabel={backLabel}
    />
  );
}

export function MissingOrderContextFrame({
  screenId,
  title,
  backTo,
  backLabel,
}: {
  screenId: string;
  title: string;
  backTo: string;
  backLabel: string;
}) {
  return (
    <MissingContextFrame
      screenId={screenId}
      title={title}
      message="Select an order to continue."
      hint="Open this screen from the order board or kitchen display."
      backTo={backTo}
      backLabel={backLabel}
    />
  );
}
